/** Max rows fetched per Enabled=true|false call when merging org-scoped type lists. */
export const ORG_SCOPED_CONFIGURATION_TYPES_FETCH_CAP = 10_000;

export type ConfigurationTypesListOrderParams = {
	"OrderBy.Fields"?: string[];
	"OrderBy.Descending"?: boolean;
};

export type OrgScopedConfigurationTypesQuery =
	ConfigurationTypesListOrderParams & {
		OrganizationId?: string;
		SubOrganizationId?: string;
		Enabled?: boolean;
	};

export function isOrgScopedConfigurationTypesQuery(
	input?: OrgScopedConfigurationTypesQuery,
): boolean {
	return !!(input?.OrganizationId || input?.SubOrganizationId);
}

export function stripEnabledParam<T extends { Enabled?: boolean }>(
	input?: T,
): Omit<T, "Enabled"> | undefined {
	if (!input) return undefined;
	const { Enabled: _enabled, ...rest } = input;
	return rest;
}

export function resolveMergedConfigurationTypeEnabled(
	row: { enabled?: boolean },
	fromDisabledFetch: boolean,
): boolean {
	if (fromDisabledFetch) return false;
	if (row.enabled === false) return false;
	return row.enabled ?? true;
}

export function mergeConfigurationTypesById<
	T extends { id?: string; enabled?: boolean },
>(enabledRows: T[], disabledRows: T[]): T[] {
	const byId = new Map<string, T>();
	for (const row of enabledRows) {
		if (row.id) {
			byId.set(row.id, {
				...row,
				enabled: resolveMergedConfigurationTypeEnabled(row, false),
			});
		}
	}
	for (const row of disabledRows) {
		if (row.id) {
			byId.set(row.id, {
				...row,
				enabled: resolveMergedConfigurationTypeEnabled(row, true),
			});
		}
	}
	return [...byId.values()];
}

export function sortConfigurationTypes<T>(
	rows: T[],
	input: ConfigurationTypesListOrderParams | undefined,
	accessors: Record<string, (row: T) => string | number | null | undefined>,
	defaultField: string,
): T[] {
	const fields = input?.["OrderBy.Fields"] ?? [defaultField];
	const descending = input?.["OrderBy.Descending"] ?? false;
	const field = fields[0] ?? defaultField;
	const accessor = accessors[field] ?? accessors[defaultField];
	if (!accessor) return rows;

	return [...rows].sort((a, b) => {
		const av = accessor(a);
		const bv = accessor(b);
		const cmp = String(av).localeCompare(String(bv), undefined, {
			sensitivity: "base",
			numeric: true,
		});
		return descending ? -cmp : cmp;
	});
}

export function sliceConfigurationTypesPage<T>(
	rows: T[],
	start: number,
	count: number,
): T[] {
	const startIndex = Math.max(0, start - 1);
	return rows.slice(startIndex, startIndex + count);
}
